<template>
    <ContentRiskActions v-if="isEditingEmailBroadcast" v-slot="contentRiskProps">
        <EmailBroadcastEditorPage
            v-bind="contentRiskProps"
            :class="{ 'standard-header-spacing': !coreConfig.isFullWidthHeaderEnabled }"
            :broadcast-id="editEmailBroadcastId"
            :from-contact-list-redirect="fromContactListRedirect"
            :list-id="listId"
            :core-config="coreConfig"
            @back="closeEmailBroadcastEditor"
            @update-broadcast-id="editEmailBroadcastId = $event"
        />
    </ContentRiskActions>

    <div
        v-else
        class="broadcast-page-wrapper"
        :class="{ 'standard-header-spacing': !coreConfig.isFullWidthHeaderEnabled }"
    >
        <div class="broadcast-page-content">
            <section id="broadcast-actions">
                <h2>{{ $options.MESSAGE_CONSTANTS.BROADCASTS }}</h2>

                <DsIconButton
                    v-show="showSmsSettings && numberProvisioned"
                    name="settings"
                    class="settings"
                    data-qa="sms-settings-button"
                    @click="smsBroadcastSettings"
                />

                <DsDropdown
                    class="new-broadcast wide-menu"
                    position="bottom-end"
                    disable-scroll
                    :is-open="isBroadcastActionDropdownOpen"
                    @close="hideBroadcastActionDropdown"
                >
                    <template #default>
                        <DsFilledButton
                            trailing-icon="chevron-down"
                            @click="showBroadcastActionDropdown"
                        >
                            {{ $options.MESSAGE_CONSTANTS.CREATE }}
                        </DsFilledButton>
                    </template>

                    <template #menu>
                        <CustomMultiselectMenu
                            data-qa="new-broadcast-menu"
                            :options="options"
                            show-option-modifier-prop="isNew"
                            categorized
                            @input="menuActions"
                        >
                            <template v-slot:option="slotProps">
                                <slot name="option" v-bind="slotProps" />
                            </template>

                            <template v-slot:optionModifier="{ option }">
                                <DsStatus :data-qa="`${option.value}-status`" emphasis="high" type="good">
                                    {{ $options.MESSAGE_CONSTANTS.NEW_TYPE }}
                                </DsStatus>
                            </template>
                        </CustomMultiselectMenu>
                    </template>
                </DsDropdown>
            </section>

            <DsTabset
                id="tab-element"
            >
                <DsTab
                    :label="$options.MESSAGE_CONSTANTS.EMAIL"
                    :action="() => showSmsSettings = false"
                    data-qa="email-tab"
                    selected
                >
                    <section>
                        <div id="mail-broadcast">
                            <EmailBroadcastPage
                                :last-sent-email-broadcast-id="lastSentEmailBroadcastId"
                                :user-locale="coreConfig.userLocale"
                                :is-apple-privacy-issues-enabled="coreConfig.isApplePrivacyIssuesEnabled"
                                :is-bard-builder-disabled="coreConfig.isBardBuilderDisabled"
                                :show-broadcasts-page-apple-privacy-guide-initial-value="coreConfig.showBroadcastsPageApplePrivacyGuide"
                                :show-email-opens-initial-value="coreConfig.showEmailOpens"
                                @new-email="newEmail"
                                @new-unlayer-email="newUnlayerEmail"
                                @edit-unlayer-broadcast="editUnlayerBroadcast"
                            />
                        </div>
                    </section>
                </DsTab>

                <DsTab
                    v-if="coreConfig.smsBroadcastEnabled"
                    :label="$options.MESSAGE_CONSTANTS.TEXT"
                    :action="() => showSmsSettings = true"
                    data-qa="sms-tab"
                >
                    <section>
                        <div v-if="showSetupState && !loadingSetupState" class="setup-state">
                            <img class="setup-image" src="/resources/broadcast/images/smsBroadcast/sms-broadcast-ftu-title.svg" />

                            <span class="setup-title">{{ $t('email.broadcast.broadcastsPage.smsTab.setup.title') }}</span>

                            <span class="setup-description">{{ $t('email.broadcast.broadcastsPage.smsTab.setup.description') }}</span>

                            <DsFilledButton class="setup-button" @click="setupSmsBroadcast">
                                {{ $t('email.broadcast.broadcastsPage.smsTab.setup.button') }}
                            </DsFilledButton>
                        </div>

                        <SmsBroadcastEmptyState
                            v-if="!showSetupState && showEmptyState && !loadingEmptyState"
                            @create-sms-broadcast="newSms"
                        />

                        <div v-if="showFailureState" data-qa="error-state-placeholders">
                            <div v-for="i in 3" :key="i">
                                <DsPlaceholder
                                    :rows="[{ height: '5rem', boxes: [2, 1, 1, 1] }]"
                                    :loading="true"
                                />
                            </div>
                        </div>

                        <SmsBroadcastPage
                            v-show="!showSetupState && !showEmptyState && !loadingSetupState"
                            :account="smsAccount"
                            :sms-account-verification="smsAccountVerification"
                            :is-kmn-branded-opt-in-enabled="coreConfig.isKmnBrandedOptInEnabled"
                            :kmn-phone-verification-enabled="coreConfig.kmnPhoneVerificationEnabled"
                            :app-id="coreConfig.appId"
                            :user-id="coreConfig.userId"
                            :loading="loading"
                            :broadcasts="broadcasts"
                            :limit="limit"
                            :no-more-data="noMoreData || showSmsBuilderModal"
                            @load="loadSmsBroadcasts"
                            @reload="reload"
                            @filter-updated="updateFilter"
                            @search-updated="updateSearch"
                            @edit-broadcast="editBroadcast"
                            @copy-broadcast="copyBroadcast"
                            @resume-broadcast="resumeBroadcast"
                            @kmn-verification-form-closed="handleCloseKmnVerificationModal"
                        />
                    </section>
                </DsTab>
            </DsTabset>

            <TextMarketingSettingsModal
                v-if="isSettingsModalOpen"
                :account="smsAccount"
                :is-billing-changes-enabled="coreConfig.isBillingChangesEnabled"
                @close="isSettingsModalOpen = false"
                @update="handleAccountUpdate"
            />

            <SmsBroadcastBuilderModal
                v-if="showSmsBuilderModal"
                data-qa="sms-broadcast-builder-modal"
                :is-open="showSmsBuilderModal"
                :from-number="fromNumber"
                :user-id="coreConfig.userId"
                :app-id="coreConfig.appId"
                :broadcast-id="broadcastId"
                :copied-broadcast-id="copiedBroadcastId"
                :title="title"
                :message-content="messageContent"
                :sms-broadcast-enabled="coreConfig.smsBroadcastEnabled"
                :sms-account="smsAccount"
                :sms-account-verification="smsAccountVerification"
                :is-billing-changes-enabled="coreConfig.isBillingChangesEnabled"
                @close="hideSmsBuilderModal"
                @update="handleAccountUpdate"
            />

            <SmsSetupModal
                :is-open="isSmsBroadcastSetupModalOpen"
                :event-source="'Broadcasts'"
                @close="isSmsBroadcastSetupModalOpen = false"
                @next="closeSmsSetupModal"
            />

            <SmsSetupCompleteModal
                :from-number="provisionedPhoneNumber"
                :is-billing-changes-enabled="coreConfig.isBillingChangesEnabled"
                :account="smsAccount"
                :is-open="isSmsBroadcastSetupCompleteModalOpen"
                @close="closeSmsBroadcastSetUpCompleteModal"
                @update="handleAccountUpdate"
            />

            <KmnVerificationForm
                v-if="showKmnVerificationModal"
                :is-open="isKmnVerificationModalOpen"
                :kmn-account="smsAccount"
                :source="kmnVerificationSource"
                :is-kmn-branded-opt-in-enabled="coreConfig.isKmnBrandedOptInEnabled"
                :kmn-phone-verification-enabled="coreConfig.kmnPhoneVerificationEnabled"
                data-qa="kmn-verification-modal"
                @close="handleCloseKmnVerificationModal"
                @complete="handleCompleteKmnVerificationModal"
            />

            <EmailComplianceModal v-if="displayEmailComplianceModal" @close="closeEmailComplianceModal" />

            <portal-target name="root" multiple />
        </div>
    </div>
</template>

<script>
import EmailBroadcastEditorPage from '@/components/broadcast/email/EmailBroadcastEditorPage';
import CustomMultiselectMenu from '@/components/shared/CustomMultiselectMenu';
import SmsBroadcastPage from '@/components/broadcast/sms/SmsBroadcastPage';
import EmailBroadcastPage from '@/components/broadcast/email/EmailBroadcastPage';
import EmailComplianceModal from '@/components/broadcast/email/EmailComplianceModal';
import SmsBroadcastEmptyState from '@/components/broadcast/sms/SmsBroadcastEmptyState';
import SmsBroadcastBuilderModal from '@/components/broadcast/sms/SmsBroadcastBuilderModal';
import KmnVerificationForm from '@/components/broadcast/sms/KmnVerificationForm';
import ContentRiskActions from '@/components/unlayer/ContentRiskActions';
import {
    getBroadcastOptions,
    getSmsAccount,
    getSmsAccountVerification,
    getSmsBroadcasts,
} from '@/api/broadcasts/smsBroadcasts';
import {
    getIsEmailLockedDown,
    createList,
} from '@/api/broadcasts/emailBroadcast';
import {
    MESSAGE_CONSTANTS,
    NEW_BROADCAST_TYPES,
    OPTIONS_LABELS,
} from '@/components/broadcast/constants/emailBroadcast.constants.js';

import SmsSetupModal from '@/components/automatedSms/setup/SmsSetupModal';
import SmsSetupCompleteModal from '@/components/automatedSms/setup/SmsSetupCompleteModal';
import TextMarketingSettingsModal from '@/components/automatedSms/TextMarketingSettingsModal';
import { trackSmsAccountProvisioningStart, trackSmsBroadcastCreated } from '@/components/automatedSms/smsAnalytics';
import {
    KMN_VERIFICATION_STATUS_TYPE,
} from '@/components/broadcast/constants/smsBroadcast.constants.js';

const VERIFICATION_DISPLAY_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.EXPIRED,
    KMN_VERIFICATION_STATUS_TYPE.RESUBMISSION,
    KMN_VERIFICATION_STATUS_TYPE.NOT_VERIFIED,
];

const PERMANENT_VERIFICATION_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.APPROVED,
    KMN_VERIFICATION_STATUS_TYPE.REJECTED,
];

export default {
    MESSAGE_CONSTANTS,

    components: {
        EmailBroadcastEditorPage,
        CustomMultiselectMenu,
        SmsBroadcastPage,
        EmailBroadcastPage,
        SmsBroadcastEmptyState,
        SmsBroadcastBuilderModal,
        SmsSetupModal,
        SmsSetupCompleteModal,
        TextMarketingSettingsModal,
        EmailComplianceModal,
        KmnVerificationForm,
        ContentRiskActions,
    },

    props: {
        coreConfig: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            smsAccount: {},
            smsAccountVerification: {},
            showSmsSettings: false,
            isSettingsModalOpen: false,
            hasSmsBroadcasts: false,
            isFtuCompleted: false,
            isSmsBroadcastSetupModalOpen: false,
            isSmsBroadcastSetupCompleteModalOpen: false,
            isBroadcastActionDropdownOpen: false,
            provisionedPhoneNumber: '',
            loadingSetupState: false,
            loadingEmptyState: false,
            options: [],
            showSmsBuilderModal: false,
            broadcastId: 0,
            title: '',
            messageContent: '',
            copiedBroadcastId: 0,
            broadcasts: [],
            loading: false,
            pageNumber: 0,
            limit: 20,
            filter: {},
            search: '',
            noMoreData: false,
            showFailureState: false,
            isEmailDisabled: false,
            displayEmailComplianceModal: false,
            isKmnVerificationModalOpen: false,
            kmnVerificationSource: 'New broadcast',
            isEditingEmailBroadcast: false,
            editEmailBroadcastId: null,
            lastSentEmailBroadcastId: null,
            listId: null,
            fromContactListRedirect: false,
        };
    },

    async created() {
        if (this.hasRequiredReportParams) await this.setContactListId();
    },

    async beforeMount() {
        try {
            this.loadingSetupState = true;
            this.smsAccount = await getSmsAccount() || {};

            await this.loadSmsAccountVerification();

            this.isFtuCompleted = this.smsAccount?.status === 'ACTIVE';

            this.loadingSetupState = false;
        } catch (error) {
            this.isFtuCompleted = false;
            this.showFailureState = true;
            this.$error({
                message: this.$t('email.broadcast.broadcastsPage.smsBroadcast.errors.loading'),
            });
        }

        this.checkBroadcasts();

        try {
            const { data } = await getBroadcastOptions();

            this.options = data.map(this.formatBroadcastOptions);
        } catch (error) {
            this.$error({
                message: MESSAGE_CONSTANTS.NO_ACCESS,
            });
        }

        this.checkEmailLockdown();
    },

    computed: {
        hasRequiredReportParams() {
            return Boolean(this.coreConfig.reportClass && (this.coreConfig.reportStateId || this.coreConfig.filterId) && this.coreConfig.fromReport);
        },

        reportParams() {
            const { reportClass, reportStateId, fromReport, filterId } = this.coreConfig;
            const validFilterId = filterId || (fromReport ? 0 : null)

            return { reportClass, reportStateId, filterId: validFilterId };
        },

        showSetupState() {
            return !this.smsAccount?.phoneNumber;
        },

        showEmptyState() {
            return !this.hasSmsBroadcasts && !this.loading;
        },

        numberProvisioned() {
            return Boolean(this.smsAccount?.phoneNumber);
        },

        fromNumber() {
            return this.smsAccount?.phoneNumber || '';
        },

        isFormValidToDisplay() {
            return !this.smsAccount?.verificationStatus || VERIFICATION_DISPLAY_TYPES.includes(this.smsAccount.verificationStatus);
        },

        showKmnVerificationModal() {
            return this.smsAccount?.phoneNumber && this.coreConfig.kmnPhoneVerificationEnabled && this.isFormValidToDisplay;
        },
    },

    methods: {
        async checkEmailLockdown() {
            try {
                this.isEmailDisabled = await getIsEmailLockedDown();
            } catch (error) {
                this.isEmailDisabled = false;
            }
        },

        async setContactListId() {
            try {
                this.listId = await createList(this.reportParams);

                this.newUnlayerEmail(true);
            } catch (error) {
                this.listId = null;
            }
        },

        openEmailComplianceModal() {
            this.displayEmailComplianceModal = true;
        },

        closeEmailComplianceModal() {
            this.displayEmailComplianceModal = false;
        },

        async checkBroadcasts() {
            try {
                this.loadingEmptyState = true;
                const broadcasts = await getSmsBroadcasts({ limit: 1 });

                this.hasSmsBroadcasts = broadcasts.length > 0;
            } catch (error) {
                this.hasSmsBroadcasts = false;
            } finally {
                this.loadingEmptyState = false;
            }
        },

        formatBroadcastOptions(container) {
            const { category } = container;

            return ({
                ...container,
                category: {
                    ...category,
                    label: this.getLabelFromOption(category),
                    isNew: NEW_BROADCAST_TYPES.includes(category.value),
                },
            });
        },

        getLabelFromOption(option) {
            return OPTIONS_LABELS[option.value] || option.label;
        },

        handleAccountUpdate(updatedAccount) {
            this.smsAccount = {
                ...this.smsAccount,
                ...updatedAccount,
            };
        },

        closeSmsSetupModal(account) {
            this.provisionedPhoneNumber = account.phoneNumber;
            this.smsAccount = account;

            this.kmnVerificationSource = 'New provision';
            this.isSmsBroadcastSetupModalOpen = false;

            if (this.showKmnVerificationModal) {
                this.isKmnVerificationModalOpen = true;
            } else {
                this.isSmsBroadcastSetupCompleteModalOpen = true;
            }
        },

        setupSmsBroadcast() {
            // TODO: move setup modal to BroadcastLanding.vue when this component is migrated there as well
            trackSmsAccountProvisioningStart('Broadcasts', this.isSpaApp);
            this.isSmsBroadcastSetupModalOpen = true;
        },

        closeSmsBroadcastSetUpCompleteModal() {
            this.isSmsBroadcastSetupCompleteModalOpen = false;
            this.isFtuCompleted = true;
        },

        smsBroadcastSettings() {
            this.isSettingsModalOpen = true;
        },

        hideSmsSettingsModal() {
            this.isSettingsModalOpen = false;
        },

        hideSmsBuilderModal() {
            this.broadcastId = 0;
            this.showSmsBuilderModal = false;
            this.reload();
        },

        showBroadcastActionDropdown() {
            this.isBroadcastActionDropdownOpen = true;
        },

        hideBroadcastActionDropdown() {
            this.isBroadcastActionDropdownOpen = false;
        },

        newUnlayerEmail(fromContactListRedirect = false) {
            if (this.isEmailDisabled) {
                this.openEmailComplianceModal();
            } else {
                this.fromContactListRedirect = fromContactListRedirect;
                this.isEditingEmailBroadcast = true;
            }
        },

        newEmail() {
            if (this.isEmailDisabled) {
                this.openEmailComplianceModal();
            } else {
                Infusion.Broadcast.launchBardEmailBroadcast('', false, false, '', '', false);
            }
        },

        newSms(title = '', messageContent = '') {
            if (!this.smsAccount?.phoneNumber) {
                this.setupSmsBroadcast();
            } else if (this.showKmnVerificationModal) {
                this.kmnVerificationSource = 'New broadcast';
                this.isKmnVerificationModalOpen = true;
            } else {
                this.broadcastId = 0;
                this.copiedBroadcastId = 0;
                this.showSmsBuilderModal = true;
                this.title = title;
                this.messageContent = messageContent;
                trackSmsBroadcastCreated(this.isSpaApp);
            }
        },

        closeAndUpdateKmn() {
            this.isKmnVerificationModalOpen = false;
            this.smsAccount = {
                ...this.smsAccount,
                isPhoneNumberVerified: true,
            };
            this.loadSmsAccountVerification();
        },

        resumeBroadcast() {
            this.closeAndUpdateKmn();
            this.reload();
            this.$toast({ message: MESSAGE_CONSTANTS.BROADCAST_RESUME_SUCCESS });
        },

        copyBroadcast(id) {
            this.closeAndUpdateKmn();
            this.copiedBroadcastId = id;
            this.broadcastId = 0;
            this.showSmsBuilderModal = true;
        },

        editBroadcast(id) {
            this.closeAndUpdateKmn();
            this.broadcastId = id;
            this.showSmsBuilderModal = true;
        },

        newFax() {
            this.callBroadcastWizard('Fax');
        },

        newLetter() {
            this.callBroadcastWizard('Letter');
        },

        newVoice() {
            this.callBroadcastWizard('Voice Broadcast');
        },

        newFulfillmentList() {
            this.callBroadcastWizard('Fulfillment List');
        },

        newQueueFulfillmentList() {
            this.callBroadcastWizard('Queued Fulfillment List');
        },

        callBroadcastWizard(broadcastType) {
            Infusion.Broadcast.launchWizard('', '', broadcastType, false, false, '', '');
        },

        menuActions({ value }) {
            this.hideBroadcastActionDropdown();

            if (this[value]) this[value]();
        },

        reload() {
            this.pageNumber = 0;
            this.broadcasts = [];
            this.loadSmsBroadcasts();
            this.checkBroadcasts();
        },

        async loadSmsBroadcasts() {
            this.loading = true;

            const {
                pageNumber,
                limit,
                search,
            } = this;

            const filter = this.filter.value || null;

            const payload = {
                appId: this.coreConfig.appId,
                completionStatus: filter,
                pageNumber,
                limit,
                search,
            };

            try {
                const response = await getSmsBroadcasts(payload);

                this.pageNumber += 1;
                this.broadcasts = this.broadcasts.concat(response);
                this.noMoreData = response.length < limit;
                this.loading = false;
            } catch (error) {
                this.$error({
                    message: this.$t('email.broadcast.broadcastsPage.smsBroadcast.errors.loading'),
                });
                this.noMoreData = true;
            }
        },

        updateFilter(value) {
            this.filter = value;
            this.reload();
        },

        updateSearch(value) {
            this.search = value;
            this.reload();
        },

        async loadSmsAccountVerification() {
            try {
                if (PERMANENT_VERIFICATION_TYPES.includes(this.smsAccountVerification.status)) {
                    return;
                }

                if (this.smsAccount.phoneNumber) {
                    const { smsAccount: { phoneNumber } } = this;

                    this.smsAccountVerification = await getSmsAccountVerification(phoneNumber);
                }
            } catch {
                this.$error({
                    message: this.$t('sms.verification.error.loadingVerificationStatus'),
                });
            }
        },

        handleCloseKmnVerificationModal() {
            this.isKmnVerificationModalOpen = false;

            this.loadSmsAccountVerification();
        },

        handleCompleteKmnVerificationModal() {
            this.isKmnVerificationModalOpen = false;

            this.smsAccount = {
                ...this.smsAccount,
                verificationStatus: KMN_VERIFICATION_STATUS_TYPE.PENDING_VERIFICATION,
            };

            if (this.kmnVerificationSource === 'New provision') {
                this.isSmsBroadcastSetupCompleteModalOpen = true;
            } else {
                this.newSms();
            }

            this.loadSmsAccountVerification();
        },

        editUnlayerBroadcast(id) {
            this.isEditingEmailBroadcast = true;
            this.editEmailBroadcastId = id;
        },

        closeEmailBroadcastEditor(broadcastId = null) {
            this.lastSentEmailBroadcastId = broadcastId;
            this.isEditingEmailBroadcast = false;
            this.editEmailBroadcastId = null;
        },
    },
};
</script>

<style lang="scss" scoped>
.broadcast-page-wrapper {
    display: flex;
    justify-content: center;
    padding: 24px 24px 32px 32px;
}

.standard-header-spacing {
    margin-top: 1.5rem;
}

.broadcast-page-content {
    width: 100%;
    max-width: 1200px;
    padding: 20px 20px 10px;
    position: relative;
}

#tab-element {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
}
</style>

<style lang="scss">
.broadcast-page-content #tab-element > .tab-header ul.tablist {
    border-bottom: none;
}

.broadcast-page-content #tab-element > .tab-header ul.tablist > li.active {
    border-bottom-width: 2px;
}

.broadcast-page-content #tab-element > .tab-content {
    position: relative;
    left: 0;
    top: 0;
    clear: both;
    visibility: visible;
}

.broadcast-page-content #broadcast-actions {
    display: grid;
    grid-template-areas: "title settings newbroadcast";
    grid-template-columns: 74% 5% 15%;
    grid-gap: 1rem;
    align-items: center;
    width: 100%;
}

.broadcast-page-content #broadcast-actions > h2 {
    grid-area: title;
}

.broadcast-page-content #broadcast-actions > .settings {
    grid-area: settings;
    margin: auto;
    padding: 0.45rem;
}

.broadcast-page-content #broadcast-actions > .new-broadcast {
    grid-area: newbroadcast;
    width: max-content;
}

.wide-menu .dropdown-menu.open {
    width: 15.625rem;
}

.broadcast-page-content .modal-content-wrapper .modal-header .navigation-button  {
    margin: 0;
}

.new-broadcast ul {
    margin: 0;
}

.new-broadcast .menu-item.breadcrumb .option-text {
    font-weight: 400;
    font-size: 1rem;
}

.page_broadcastLandingPage #lightbox td {
    padding: 0;
}

#broadcast-draft-lightbox-modal-container #lightbox .modal-body {
    height: auto;
}

#broadcast-draft-lightbox-modal-container #lightbox .modal-footer {
    display: block;
    visibility: visible;
}

#broadcast-draft-lightbox-modal-container #lightbox {
    height: auto;
    width: 425px !important;
    border-radius: 0;
}

#broadcast-draft-lightbox-modal-container #lightbox .container-close {
    visibility: visible;
    left: auto;
    grid-area: auto;
    border-bottom: 0;
    width: 25px;
}

.page_broadcastLandingPage .button.loading,
.email-broadcast-editor-page .button.loading {
    display: block !important;
}

#tab-element .tab:nth-child(1) {
    padding-left: 0;
}

.setup-title {
    text-align: center;
    font-family: $font-family-secondary-title;
    font-size: $font-size-jumbo;
    font-weight: 700;
    line-height: $line-height-xxl;
    margin: $spacing-300 0 $spacing-200 $spacing-400;
    width: 50%;
}

.setup-state {
    margin: $spacing-400;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.setup-description {
    font-size: $font-size-md;
    line-height: $line-height-lg;
    color: $color-ink-900;
    margin: 0 0 $spacing-400 $spacing-400;
    text-align: center;
    width: 75%;
}
</style>
